import '.p{position:absolute;}.ssr-plugin'
import '.bp{top:calc(100%);}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.c{align-items:center;}.ssr-plugin'
/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  Children,
  useCallback,
  ReactElement,
} from 'react'
import { styled, type Style } from 'inlines'
import { Text } from '../typography/Text'
import useMediaQuery from '@app/util/useMediaQuery'
import { LinkWrapper } from '../link/LinkWrapper'
import { Image } from '../image/Image'

type TabsContextType = {
  selectedTab: string
}

const TabsContext = createContext<TabsContextType | undefined>(undefined)

export type GroupProps<T extends string> = {
  selectedTab: T
  children: ReactElement<TabProps<T>>[]
  style?: Style
}

const Group = <T extends string>({
  children,
  selectedTab,
  style,
}: GroupProps<T>) => {
  const groupRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    const el = groupRef.current
    if (!el) {
      return
    }

    const headerHeightValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height')
      .trim()
    const headerHeight = headerHeightValue ? parseFloat(headerHeightValue) : 0

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry && !entry.isIntersecting) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          })
        }
        observer.disconnect()
      },
      { rootMargin: `-${headerHeight}px 0px 0px 0px` },
    )

    observer.observe(el)

    return () => observer.disconnect()
  }, [selectedTab])

  return (
    <TabsContext.Provider value={{ selectedTab }}>
      <div
        ref={groupRef}
        style={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          display: 'inline-flex',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          userSelect: 'none',
          scrollMarginTop: 'var(--header-height)',
          ...style,
        }}
      >
        {Children.map(children, (child) => child)}
      </div>
    </TabsContext.Provider>
  )
}

export type TabProps<T extends string> = {
  children?: JSX.Element
  tabKey: T
  label: string
  onSelectTab: (key: T) => void
}

const Tab = <T extends string>({
  children,
  tabKey,
  label,
  onSelectTab,
}: TabProps<T>) => {
  const context = useContext(TabsContext)

  if (!context) {
    throw new Error('Tabs.Tab must be used within a Tabs.Group')
  }

  const [subMenuShown, setSubMenuShown] = useState(false)
  const { selectedTab } = context
  const isSelected = selectedTab === tabKey

  const tabStyle: Style = {
    padding: '1.5rem',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--primary-blue)',
    },
    borderBottom: `2px ${
      isSelected ? 'var(--primary-blue)' : 'transparent'
    } solid`,
  }

  const textStyle: Style = {
    wordWrap: 'break-word',
    whiteSpace: 'nowrap',
    color: isSelected ? 'var(--primary-blue)' : 'var(--shades-blue-300)',
    '&:hover': {
      color: 'var(--primary-blue)',
    },
  }

  const onTabSelect = useCallback(() => {
    onSelectTab(tabKey)
  }, [tabKey, onSelectTab])

  const renderSubMenu = useCallback(() => {
    if (!children || !subMenuShown) {
      return null
    }
    return (
      <div className="p bp"
      >
        {children}
      </div>
    )
  }, [subMenuShown, children])

  return (
    <styled.div
      data-state={isSelected ? 'Selected' : 'Default'}
      style={tabStyle}
      onClick={onTabSelect}
      onMouseOver={() => setSubMenuShown(true)}
      onMouseOut={() => setSubMenuShown(false)}
    >
      <Text.Medium style={textStyle}>{label}</Text.Medium>
      {renderSubMenu()}
    </styled.div>
  )
}

export type HeaderTabProps<T extends string> = {
  tabKey: T
  label: string
  icon: string
  activeIcon?: string
  href: string
  onSelectTab: (key: T) => void
}

const HeaderTab = <T extends string>({
  tabKey,
  label,
  icon,
  activeIcon,
  href,
  onSelectTab,
}: HeaderTabProps<T>) => {
  const context = useContext(TabsContext)
  const isMobile = useMediaQuery()

  if (!context) {
    throw new Error('Tabs.Tab must be used within a Tabs.Group')
  }

  if (!label && !icon) {
    throw new Error('Tab should either have label or icon')
  }

  const { selectedTab } = context
  const isSelected = selectedTab === tabKey
  const [isHovered, setIsHovered] = useState(false)

  const tabStyle: Style = {
    padding: isMobile ? '1rem 0.5rem' : '1.5rem 1rem',
    justifyContent: 'center',
    alignItems: 'center',
    gap: isMobile ? '1rem' : '2.5rem',
    display: 'flex',
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--primary-blue)',
    },
    borderBottom: `3px ${
      isSelected ? 'var(--primary-blue)' : 'transparent'
    } solid`,
  }

  const textStyle: Style = {
    wordWrap: 'break-word',
    color: isSelected || isHovered ? 'var(--primary-blue)' : 'var(--gray-300)',
    fontFamily: 'FunnelDisplay',
    fontSize: isMobile ? '0.75rem' : '1.75rem',
  }

  const onTabSelect = useCallback(() => {
    onSelectTab(tabKey)
  }, [tabKey, onSelectTab])

  const showActiveIcon = (isSelected || isHovered) && activeIcon
  const displayIcon = showActiveIcon ? activeIcon : icon

  const renderedIcon = displayIcon ? (
    <Image
      src={displayIcon}
      style={{
        width: isMobile ? '1.875rem' : '2.5rem',
        height: isMobile ? '1.875rem' : '2.5rem',
        objectFit: 'contain',
      }}
    />
  ) : null

  return (
    <LinkWrapper to={href}>
      <styled.div
        data-state={isSelected ? 'Selected' : 'Default'}
        style={tabStyle}
        onClick={onTabSelect}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="a c"
          style={{
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '0.5rem' : '1rem',
          }}
        >
          {renderedIcon}
          <Text.Medium style={textStyle}>{label}</Text.Medium>
        </div>
      </styled.div>
    </LinkWrapper>
  )
}

export const Tabs = {
  Group,
  Tab,
  HeaderTab,
}
